import Loader from '@/components/Loader';
import ErrorMessage from '@/forms/_common/ErrorMessage';
import { PayloadStates } from '@lore/utils';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@lore/auth';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { Helmet } from 'react-helmet-async';
import useRouter from '@/hooks/useRouter';

const KNOWN_FLAGS = [
  {
    key: 'streamlined_account_creation',
    label: 'Streamlined Account Creation',
    description: 'User is part of the streamlined account creation process and sees new CTA cards.',
  },
  {
    key: 'cta_fellowship_training',
    label: 'CTA: Fellowship Training',
    description: 'User is invited to fellowship training via a CTA card.',
  },
  {
    key: 'cta_feedback_survey',
    label: 'CTA: Feedback Survey',
    description: 'User is invited to complete a feedback survey via a CTA card.',
  },
  {
    key: 'lower_network_barriers_for_stewards',
    label: 'Lower Network Barriers for Stewards',
    description: 'Steward relies less on network settings. Not shown "All" users or "All" partners.',
  },
];

FlagBadge.propTypes = {
  enabled: PropTypes.bool.isRequired
};
function FlagBadge({ enabled }) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        Enabled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      Disabled
    </span>
  );
}

export default function Layout() {
  const currentUser = useUser();
  const { isFetching, client } = useFeatureFlags(currentUser);
  const { history } = useRouter();
  const [customFlag, setCustomFlag] = useState('');
  const [customResult, setCustomResult] = useState(null);
  const [hasCheckedCustom, setHasCheckedCustom] = useState(false);

  function checkFlag(key) {
    if (!client || isFetching) return false;
    return client.checkGate(key);
  }

  function handleCheckCustomFlag(e) {
    e.preventDefault();
    const trimmed = customFlag.trim();
    if (!trimmed || !client) return;
    setCustomResult(client.checkGate(trimmed));
    setHasCheckedCustom(true);
  }

  function handleCustomFlagChange(e) {
    setCustomFlag(e.target.value);
    setHasCheckedCustom(false);
    setCustomResult(null);
  }

  if ([PayloadStates.FETCHING, PayloadStates.INITIAL_STATE].includes(currentUser.state)) {

    return (
      <div className="flex flex-col justify-center h-[80vh]">
        <Loader/>
      </div>
    )
  }

  if (currentUser.state !== PayloadStates.RESOLVED || !currentUser.id) {
    return (
      <div className="flex flex-col items-center">
        <ErrorMessage v2={true}>
          Error getting user
        </ErrorMessage>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 lg:p-10">
      <Helmet>
        <title>Feature Flags</title>
      </Helmet>

      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
            <button
              onClick={() => history.push(window.history.length > 1 ? -1 : '/home')}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Current flag states for{' '}
            <span className="font-medium text-gray-700">{currentUser?.data?.email}</span>
            {isFetching && (
              <span className="ml-2 text-xs text-gray-400 italic">loading...</span>
            )}
          </p>
        </div>

        {/* Known flags */}
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {KNOWN_FLAGS.map((flag) => (
            <div key={flag.key} className="flex items-start justify-between gap-4 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">{flag.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{flag.description}</p>
                <p className="text-xs font-mono text-gray-400 mt-1">{flag.key}</p>
              </div>
              <div className="shrink-0 pt-0.5">
                {isFetching ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-400">
                    —
                  </span>
                ) : (
                  <FlagBadge enabled={checkFlag(flag.key)} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Custom flag lookup */}
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-5 space-y-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">Check a flag</p>
            <p className="text-xs text-gray-500 mt-0.5">Enter any flag key to check its state for the current user.</p>
          </div>
          <form onSubmit={handleCheckCustomFlag} className="flex gap-2">
            <input
              type="text"
              value={customFlag}
              onChange={handleCustomFlagChange}
              placeholder="flag_key_name"
              className="flex-1 text-sm font-mono border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-300"
            />
            <button
              type="submit"
              disabled={!customFlag.trim() || isFetching}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Check
            </button>
          </form>
          {hasCheckedCustom && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-gray-500">Result for</span>
              <span className="text-xs font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{customFlag.trim()}</span>
              <span className="text-xs text-gray-500">:</span>
              <FlagBadge enabled={customResult} />
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400">Press F8 to open or close this page from anywhere</p>
      </div>
    </div>
  );
}
